import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, ButtonBase } from '@mui/material';
import React from 'react';
import { globalHeaderHeightNormal, globalHeaderHeightSmall } from '../GlobalHeader/constants';
import { globalInfoHeight } from '../GlobalInfo/constants';
import { globalMenuWidthSmall } from '../GlobalMenu/constants';
import { themeColors } from '../JuvareTheme';
import { useHoverSupported, useSmallDevice, useSmallOrMediumDevice } from '../hooks';
import { globalPinnedBarWidth } from './constants';
/**
 * Renders a pinned sidebar that appears beside the global menu and can contain arbitrary content.
 * Supports responsive behavior, such as collapsing on small or medium devices and respecting the state
 * of the global menu and info bar.
 *
 * @param {GlobalPinnedBarProps} props - Props for the pinned bar.
 * @param {boolean} props.menuOpen - Whether the global menu is currently open.
 * @param {boolean} props.infoOpen - Whether the global info bar is currently open.
 * @param {'expand-on-hover' | 'icon' | 'icon-and-label'} [props.menuMode] - Optional mode of the global menu, affects layout.
 * @param {SxProps<Theme>} [props.sx] - Optional additional styling applied to the pinned bar container.
 * @param {React.ReactNode} props.children - React children to render inside the pinned bar.
 *
 * @returns {JSX.Element} The rendered `GlobalPinnedBar` component.
 */
export const GlobalPinnedBar = (props) => {
    var _a;
    const smallDevice = useSmallDevice();
    const smallOrMediumDevice = useSmallOrMediumDevice();
    const hoverSupported = useHoverSupported() && !smallDevice;
    const [pinnedBarOpen, setPinnedBarOpen] = React.useState(false);
    const menuDisplayMode = (_a = props.menuMode) !== null && _a !== void 0 ? _a : 'expand-on-hover';
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: [
                {
                    position: 'fixed',
                    top: (smallDevice ? globalHeaderHeightSmall : globalHeaderHeightNormal) + (props.infoOpen ? globalInfoHeight : 0),
                    bottom: 0,
                    left: (props.menuOpen && hoverSupported && menuDisplayMode !== 'icon-and-label' ? globalMenuWidthSmall : 0) +
                        (props.menuOpen && hoverSupported && menuDisplayMode === 'icon-and-label' ? globalPinnedBarWidth : 0) +
                        (smallOrMediumDevice && !pinnedBarOpen ? -globalPinnedBarWidth : 0),
                    width: globalPinnedBarWidth,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: (theme) => theme.shadows[2],
                    zIndex: (theme) => theme.zIndex.appBar - 4,
                    '@media print': {
                        display: 'none',
                    },
                    transition: (theme) => theme.transitions.create('left') + ',' + theme.transitions.create('margin-right', { duration: 400 }),
                },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ] }, props.children),
        smallOrMediumDevice && (React.createElement(ButtonBase, { centerRipple: true, focusRipple: true, sx: {
                position: 'fixed',
                boxSizing: 'border-box',
                top: `50%`,
                marginTop: '-40px',
                left: (props.menuOpen && hoverSupported ? globalMenuWidthSmall : 0) +
                    (smallOrMediumDevice && pinnedBarOpen ? globalPinnedBarWidth : 0),
                width: 32,
                height: 80,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
                overflow: 'hidden',
                color: themeColors.icon.link,
                bgcolor: 'background.paper',
                boxShadow: (theme) => theme.shadows[3],
                transition: (theme) => theme.transitions.create('left'),
                zIndex: (theme) => theme.zIndex.appBar - 5,
                '@media print': {
                    display: 'none',
                },
            }, onClick: () => setPinnedBarOpen(!pinnedBarOpen) }, pinnedBarOpen ? React.createElement(KeyboardArrowLeftIcon, { color: 'inherit' }) : React.createElement(KeyboardArrowRightIcon, { color: 'inherit' })))));
};
